import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import BaseButton from "../../../components/ui/BaseButton";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import AddEditResumeModal from "../../ProfileSettings/ProfileSetting/components/AddEditResumeModal";
import { deleteUserResume } from "../../../services/api/digitalAsset";
import { showToast } from "../../../store/toast/toastSlice";

// Resume upload step for the Complete-your-profile flow. Resume is mandatory:
// when none is uploaded we surface a required-field error so the user knows the
// section is still incomplete.
const Resume = ({ userId, resume, refetchMe }) => {
  const dispatch = useDispatch();
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const hasResume = !!resume;

  const { isPending: isDeleting, mutateAsync: deleteResumeMutation } = useMutation({
    mutationFn: () => deleteUserResume(userId),
    enabled: false,
  });

  const handleDelete = async () => {
    try {
      await deleteResumeMutation();
      setIsDeleteConfirmOpen(false);
      dispatch(showToast({ message: "Resume deleted successfully", type: "success" }));
      refetchMe();
    } catch (error) {
      dispatch(showToast({ message: "Failed to delete resume", type: "error" }));
    }
  };

  return (
    <section className="py-4 px-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {hasResume ? (
          <div className="flex items-center justify-between bg-light rounded-md py-3 px-4 border w-full sm:w-auto sm:flex-1 sm:max-w-md min-w-0">
            <div className="flex items-center min-w-0">
              <i className="fa-regular fa-file-pdf text-xl text-red-500 shrink-0"></i>
              <p className="ml-3 text-sm truncate">{resume.filename}</p>
            </div>
            <div className="flex items-center ml-3 shrink-0">
              <a
                href={resume.url}
                download
                target="_blank"
                rel="noreferrer"
                type="application/octet-stream"
              >
                <i className="fa-solid fa-download mr-3 text-emerald-700 text-base cursor-pointer"></i>
              </a>
              {
                <i
                  className="fa-regular fa-trash-can text-red-600 text-base cursor-pointer"
                  title="Delete resume"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                ></i>
              }
            </div>
          </div>
        ) : (
          <p className="font-normal !text-red-600">
            Resume is required. Please upload your resume (PDF, DOC, or DOCX).
          </p>
        )}
        {
          <BaseButton
            color="primary"
            variant="outlined"
            className="py-[7px] px-[10px] shrink-0"
            onClick={() => setIsResumeModalOpen(true)}
          >
            {hasResume ? "Replace Resume" : "Upload Resume"}
          </BaseButton>
        }
      </div>

      {isResumeModalOpen && (
        <AddEditResumeModal
          open={isResumeModalOpen}
          close={setIsResumeModalOpen}
          existingResume={resume}
          refetchMe={refetchMe}
          targetUserId={userId}
        />
      )}

      {isDeleteConfirmOpen && (
        <BaseConfirmModal
          open={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Resume"
          description="Are you sure you want to delete your resume? This action cannot be undone."
          isLoading={isDeleting}
          onConfirm={handleDelete}
        />
      )}
    </section>
  );
};

export default Resume;
